import { useAuth } from "@/auth/auth";
import useSWR from "swr";
import type { APIError, Limrun } from "@limrun/api";
import { displayAnalyticsCredits, displayInstanceCredits } from "./analytics-credits";

// wallclockMinutes is served by the director but not yet in the published
// @limrun/api types; drop the intersections once the SDK regenerates.
export type AnalyticsRegionStats = Limrun.AnalyticsResponse.Series.Android & { wallclockMinutes?: number };
export type AnalyticsInstance = Limrun.AnalyticsResponse.Series.Instance & { wallclockMinutes?: number };
export type AnalyticsResponse = Omit<Limrun.AnalyticsResponse, "from" | "to">;
export type AnalyticsInstancesResponse = Omit<Limrun.AnalyticsInstancesResponse, "from" | "to">;

type AnalyticsOptions = {
  region?: string;
  bucket?: Limrun.AnalyticsGetParams["bucket"];
  timezone?: string;
  labels?: string;
};

type AnalyticsHookResult<T> = {
  data?: { data: T };
  error?: APIError;
  refresh: () => Promise<unknown>;
};

// Use the SDK request method until its analytics resource supports the new filters.
function analyticsParams(from: string, to: string, options?: AnalyticsOptions) {
  return {
    "startedAt[gte]": from,
    "startedAt[lte]": to,
    ...(options?.region ? { region: options.region } : {}),
    ...(options?.bucket ? { bucket: options.bucket } : {}),
    ...(options?.timezone ? { timezone: options.timezone } : {}),
    ...(options?.labels ? { labels: options.labels } : {}),
  };
}

export function useOrganizationAnalytics(
  organizationTid: string | "loading",
  from: string,
  to: string,
  options?: AnalyticsOptions,
): AnalyticsHookResult<AnalyticsResponse> {
  const auth = useAuth();
  const { data, error, mutate } = useSWR(
    organizationTid !== "loading" ? ["analytics", organizationTid, from, to, options] : null,
    async () => displayAnalyticsCredits(await auth.regionClient.get<AnalyticsResponse>("/v1/analytics", { query: analyticsParams(from, to, options) })),
  );
  return {
    data: data ? { data } : undefined,
    error,
    refresh: mutate,
  };
}

export function useOrganizationAnalyticsInstances(
  organizationTid: string | "loading",
  from: string,
  to: string,
  options?: Omit<AnalyticsOptions, "bucket">,
): AnalyticsHookResult<AnalyticsInstancesResponse> {
  const auth = useAuth();
  const { data, error, mutate } = useSWR(
    organizationTid !== "loading" ? ["analyticsInstances", organizationTid, from, to, options] : null,
    async () => displayInstanceCredits(await auth.regionClient.get<AnalyticsInstancesResponse>("/v1/analytics/instances", { query: analyticsParams(from, to, options) })),
  );
  return {
    data: data ? { data } : undefined,
    error,
    refresh: mutate,
  };
}
